import * as React from 'react'
import { bind } from 'classnames/bind'
import * as styles from '@procore/core-css/src/layouts/_tool_header.scss'

import addSubcomponents from '../utils/addSubcomponents'
import {
  SizeNS,
  DivAttributes,
  HeadingAttributes,
  SpanAttributes,
} from '../utils/types'
import IconBase from '../components/Icon'
import BaseTabs from '../components/Tabs'

const cx = bind(styles)

export namespace ToolHeaderBadgeVariantNS {
  export const beta = 'beta'
  export const new_ = 'new'
  export const trial = 'trial'
}

type ToolHeaderBadgeVariant =
  | typeof ToolHeaderBadgeVariantNS.beta
  | typeof ToolHeaderBadgeVariantNS.new_
  | typeof ToolHeaderBadgeVariantNS.trial

interface Props {
  children?: React.ReactNode
  className: string
  style: object
}

interface BadgeProps extends Props {
  variant: ToolHeaderBadgeVariant
}

interface IconProps extends Props {
  icon: string
}

export const ToolHeader = React.forwardRef<
  HTMLDivElement,
  DivAttributes & Props
>(({ children, className, ...props }, ref) => (
  <div ref={ref} className={cx('core-l-tool-header', className)} {...props}>
    {children}
  </div>
))

export const Icon = React.forwardRef<HTMLDivElement, DivAttributes & IconProps>(
  ({ className, icon = 'cog', ...props }, ref) => (
    <div ref={ref} className={cx('core-l-tool-header__icon', className)}>
      <IconBase icon={icon} {...props} size={SizeNS.sm} />
    </div>
  )
)

export const Title = React.forwardRef<
  HTMLHeadingElement,
  HeadingAttributes & Props
>(({ children, className, ...props }, ref) => (
  <h1
    ref={ref}
    className={cx('core-l-tool-header__title', className)}
    {...props}
  >
    {children}
  </h1>
))

export const Tabs = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={cx('core-l-tool-header__tabs', className)}>
      <BaseTabs {...props}>{children}</BaseTabs>
    </div>
  )
)

export const Actions = React.forwardRef<HTMLDivElement, DivAttributes & Props>(
  ({ children, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cx('core-l-tool-header__actions', className)}
      {...props}
    >
      {children}
    </div>
  )
)

export const Badge = React.forwardRef<
  HTMLDivElement,
  DivAttributes & BadgeProps
>(
  (
    {
      className,
      children = '',
      variant = ToolHeaderBadgeVariantNS.beta,
      ...props
    },
    ref
  ) => (
    <div
      ref={ref}
      className={cx(
        'core-tool-header-badge__label',
        {
          [`core-tool-header-badge__label--${variant}`]: variant,
        },
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)

ToolHeader.displayName = 'ToolHeader'

Icon.displayName = 'ToolHeader.Icon'

Title.displayName = 'ToolHeader.Title'

Tabs.displayName = 'ToolHeader.Tabs'

Actions.displayName = 'ToolHeader.Actions'

Badge.displayName = 'ToolHeader.Badge'

export default addSubcomponents(
  {
    Icon,
    Title,
    Tabs,
    Actions,
    Badge,
  },
  ToolHeader
)
